import { Box, Group, Stack } from '@mantine/core';
import { IconSkeleton, TextSkeleton, TitleSkeleton } from '@repo/foundations';
import { Handle, Position } from '@xyflow/react';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useLineageFlowContext } from '../../context/LineageFlowProvider/LineageFlowContext';
import { useStyles as useEntityNodeChildrenStyles } from '../EntityNodeChildren/EntityNodeChildren.styles';
import { ENTITY_NODE_HEADER_INTEGRATION_ICON_SIZE } from '../EntityNodeHeader/EntityNodeHeader.constants';
import { useStyles as useEntityNodeHeaderStyles } from '../EntityNodeHeader/EntityNodeHeader.styles';
import { useStyles as useEntityNodeStyles } from './EntityNode.styles';

interface EntityNodeSkeletonProps {
	id: string;
	count: number;
}

function EntityNodeSkeleton({ id, count }: EntityNodeSkeletonProps) {
	const { classes: EntityNodeClasses } = useEntityNodeStyles({
		selected: false,
		createAccess: false,
	});

	const { classes: EntityNodeHeaderClasses } = useEntityNodeHeaderStyles();

	const { classes: EntityNodeChildrenClasses } = useEntityNodeChildrenStyles({
		highlighted: false,
	});

	const { highlightedParentChildMap } = useLineageFlowContext();

	const highlightedChildren =
		count > 0
			? computed(() => highlightedParentChildMap.get(id)).get()
			: undefined;

	return (
		<Group className={EntityNodeClasses.wrapper}>
			<Stack className={EntityNodeClasses.content}>
				<Handle
					id="MAIN_TARGET"
					className={EntityNodeClasses.handle}
					type="target"
					position={Position.Left}
					isConnectable={false}
				/>
				{/* EntityNodeHeader Skeleton */}
				<Group className={EntityNodeHeaderClasses.wrapper}>
					<IconSkeleton size={ENTITY_NODE_HEADER_INTEGRATION_ICON_SIZE} />
					<Group className={EntityNodeHeaderClasses.content}>
						<Stack w="100%" spacing="3xs">
							<TitleSkeleton size="sm" width="50%" />
							<TextSkeleton size="xs" width="75%" />
						</Stack>
					</Group>
				</Group>
				{/* EntityNodeChildren Skeleton: Handles */}
				{highlightedChildren?.map((child) => (
					<Box key={`EntityNodeChildren-HandleWrapper--${child.id}`}>
						<Handle
							className={EntityNodeChildrenClasses.handle}
							id={`CHILD_TARGET--${child.id}`}
							type="target"
							position={Position.Left}
							isConnectable={false}
						/>
						<Handle
							className={EntityNodeChildrenClasses.handle}
							id={`CHILD_SOURCE--${child.id}`}
							type="source"
							position={Position.Right}
							isConnectable={false}
						/>
					</Box>
				))}
				<Handle
					id="MAIN_SOURCE"
					className={EntityNodeClasses.handle}
					type="source"
					position={Position.Right}
					isConnectable={false}
				/>
			</Stack>
		</Group>
	);
}

const ObservedEntityNodeSkeleton = observer(EntityNodeSkeleton);

EntityNodeSkeleton.displayName = 'EntityNodeSkeleton';
ObservedEntityNodeSkeleton.displayName = 'ObservedEntityNodeSkeleton';

export default ObservedEntityNodeSkeleton;
